import { ResourceType } from '__types__';
import store from 'store';
import { ResourceTypeApi } from 'v5/api/resource-types/resource-types.api';
import { WithServices } from 'v5/platform/ts/services';
import { WithErrorService } from 'v5/services/front/ErrorService/ErrorService';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { ResourceTypeBindingsService } from 'v5/services/internal/ResourceTypeBindingsService/ResourceTypeBindingsService';
import {
  mapAssignmentsToMetastructureFields,
  mapResourceTypeToAdmin,
  toFieldAssignment
} from './mapResourceTypeFromApi';
import { ResourceTypeAdminMutationService } from './ResourceTypeAdminMutationService';
import {
  syncResourceTypeCreatedToRedux,
  syncResourceTypeDeletedToRedux,
  syncResourceTypeOrderToRedux,
  syncResourceTypeUpdatedToRedux
} from './sync/syncResourceTypeToRedux';

/**
 * Write facade: API → Redux SUCCESS. RQ cache is updated by the read service’s
 * v1Event listener (same handshake as CalloutMutation → CalloutsService).
 */
export function createResourceTypeAdminMutationService(
  config: WithServices<[WithErrorService, WithMetastructureMappingService]> & {
    api: ResourceTypeApi;
    resourceTypeBindingsService: ResourceTypeBindingsService;
  }
): ResourceTypeAdminMutationService {
  const {
    api,
    resourceTypeBindingsService,
    services: { errorService, metastructureMappingService }
  } = config;
  const interceptor = errorService.withCategory(
    'ResourceTypeAdminMutationService'
  );

  function readResourceTypesFromRedux(): ResourceType[] {
    const state = store.getState() as {
      resource_types?: {
        data?: Record<number, ResourceType>;
      };
    };
    return Object.values(state.resource_types?.data ?? {});
  }

  async function mapAdmin(type: ResourceType) {
    const defs =
      await metastructureMappingService.ensureCustomFieldDefinitions();
    return mapResourceTypeToAdmin(type, defs);
  }

  return {
    async createResourceType(input) {
      const created = await interceptor.wrap(() => api.create(input))();
      syncResourceTypeCreatedToRedux(created);
      return mapAdmin(created);
    },

    async updateResourceType(id, patch) {
      const updated = await interceptor.wrap(() => api.update(id, patch))();
      syncResourceTypeUpdatedToRedux(updated);
      return mapAdmin(updated);
    },

    async archiveResourceType(id) {
      await interceptor.wrap(() => api.remove(id))();
      syncResourceTypeDeletedToRedux(id);
    },

    async unarchiveResourceType(id) {
      const updated = await interceptor.wrap(() =>
        api.update(id, { archived: false })
      )();
      syncResourceTypeUpdatedToRedux(updated);
    },

    async reorderResourceTypes(orderedIds) {
      const list = readResourceTypesFromRedux();
      const previous = list;
      const byId = new Map(list.map(type => [type.id, type]));
      const reordered: ResourceType[] = [];
      for (const [order, id] of orderedIds.entries()) {
        const type = byId.get(id);
        if (type) {
          reordered.push({ ...type, order });
        }
      }
      const nextList = [
        ...reordered,
        ...list.filter(type => !orderedIds.includes(type.id))
      ];
      // Optimistic: dispatch full list so reducer + v1Event reseed RQ completely
      // (partial list would drop archived types).
      syncResourceTypeOrderToRedux(nextList);

      try {
        const items = orderedIds.map((id, order) => ({ id, order }));
        await interceptor.wrap(() => api.reorder(items))();
      } catch (error) {
        syncResourceTypeOrderToRedux(previous);
        throw error;
      }
    },

    async setGlobalFields(resourceTypeId, fields) {
      const leanFields = fields.map(toFieldAssignment);
      const current =
        readResourceTypesFromRedux().find(type => type.id === resourceTypeId) ??
        (await api.get(resourceTypeId));
      const metastructure = {
        ...current.metastructure,
        fields: mapAssignmentsToMetastructureFields(
          leanFields,
          current.metastructure?.fields
        )
      };
      const updated = await interceptor.wrap(() =>
        api.update(resourceTypeId, { metastructure })
      )();
      syncResourceTypeUpdatedToRedux(updated);
      return mapAdmin(updated);
    },

    async setEnabledInWorkspace(input) {
      await resourceTypeBindingsService.setEnabled(input);
    },

    async setWorkspaceFields({
      organizationId,
      resourceTypeId,
      workspaceId,
      fields
    }) {
      await resourceTypeBindingsService.setWorkspaceFields({
        organizationId,
        resourceTypeId,
        workspaceId,
        workspaceFields: fields.map(toFieldAssignment)
      });
    }
  };
}
